import { useMutation, useQuery } from '@apollo/client';
import type {
  EffectiveFlagPolicy,
  FlagAmountThresholdInput,
  FlagPriority,
  FlagTypePolicyInput,
  FlagTypes,
} from '@generated/graphql';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FETCH_ORGANIZATION_FLAG_POLICIES,
  UPDATE_ORG_FLAG_POLICIES,
} from './flagPolicies.queries';
import type { FlagPolicyEnabledState } from './flagPolicies.types';
import {
  buildFlagPolicyRowView,
  buildPolicyOverrideForEnabledState,
  findPolicyOverride,
  findThresholdOverride,
  getThresholdAxis,
  isThresholdableFlagType,
  restoreInitialThresholdOnEnable,
  shouldSyncOrgPolicyFromQuery,
} from './utils/flagPolicy';
import {
  flagPolicyOverridesEqual,
  normalizePolicyOverrides,
  normalizeThresholdOverrides,
  validateFlagPolicyOverrides,
} from './utils/validateFlagPolicyOverrides';

type UseFlagPolicyEditorOptions = {
  organizationId: string | undefined;
  canWrite: boolean;
};

export const useFlagPolicyEditor = ({
  organizationId,
  canWrite,
}: UseFlagPolicyEditorOptions) => {
  const { showToast } = useGlobalToast();
  const { t } = useTranslation(['settings', 'shared', 'reconciliation']);

  const [flagTypePolicies, setFlagTypePolicies] = useState<
    FlagTypePolicyInput[]
  >([]);
  const [flagAmountThresholds, setFlagAmountThresholds] = useState<
    FlagAmountThresholdInput[]
  >([]);
  const [initialPolicies, setInitialPolicies] = useState<FlagTypePolicyInput[]>(
    [],
  );
  const [initialThresholds, setInitialThresholds] = useState<
    FlagAmountThresholdInput[]
  >([]);
  const [syncedOrganizationId, setSyncedOrganizationId] = useState<
    string | undefined
  >();

  const {
    data: orgData,
    loading: orgLoading,
    refetch: refetchOrg,
  } = useQuery(FETCH_ORGANIZATION_FLAG_POLICIES, {
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: 'network-only',
  });

  const applyOrgPolicyState = useCallback(
    (
      org:
        | {
            flagTypePolicies?: FlagTypePolicyInput[] | null;
            flagAmountThresholds?: FlagAmountThresholdInput[] | null;
          }
        | null
        | undefined,
    ) => {
      if (!org) {
        return;
      }

      const policies = (org.flagTypePolicies ?? []).filter(Boolean);
      const thresholds = (org.flagAmountThresholds ?? []).filter(Boolean);
      setFlagTypePolicies(policies);
      setFlagAmountThresholds(thresholds);
      setInitialPolicies(policies);
      setInitialThresholds(thresholds);
    },
    [],
  );

  const isDirty = useMemo(
    () =>
      !flagPolicyOverridesEqual(
        flagTypePolicies,
        initialPolicies,
        flagAmountThresholds,
        initialThresholds,
      ),
    [
      flagTypePolicies,
      initialPolicies,
      flagAmountThresholds,
      initialThresholds,
    ],
  );

  useEffect(() => {
    if (!organizationId) {
      setSyncedOrganizationId(undefined);
      return;
    }

    if (
      syncedOrganizationId != null &&
      syncedOrganizationId !== organizationId
    ) {
      setSyncedOrganizationId(undefined);
      setFlagTypePolicies([]);
      setFlagAmountThresholds([]);
      setInitialPolicies([]);
      setInitialThresholds([]);
    }
  }, [organizationId, syncedOrganizationId]);

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    const org = orgData?.fetchOrganization?.organization;
    if (
      !shouldSyncOrgPolicyFromQuery({
        activeOrganizationId: organizationId,
        fetchedOrganizationId: org?.id,
        syncedOrganizationId,
        isDirty,
      })
    ) {
      return;
    }

    applyOrgPolicyState(org);
    setSyncedOrganizationId(organizationId);
  }, [
    organizationId,
    orgData,
    isDirty,
    syncedOrganizationId,
    applyOrgPolicyState,
  ]);

  const effectivePolicies: EffectiveFlagPolicy[] = useMemo(() => {
    const org = orgData?.fetchOrganization?.organization;
    if (!org || org.id !== organizationId) {
      return [];
    }
    return org.effectiveFlagPolicies ?? [];
  }, [orgData, organizationId]);

  const isOrgSynced = useMemo(
    () =>
      !!organizationId &&
      !orgLoading &&
      syncedOrganizationId === organizationId &&
      orgData?.fetchOrganization?.organization?.id === organizationId,
    [organizationId, orgLoading, orgData, syncedOrganizationId],
  );

  const rows = useMemo(
    () =>
      effectivePolicies.map((effective) =>
        buildFlagPolicyRowView(
          effective,
          flagTypePolicies,
          flagAmountThresholds,
          initialPolicies,
        ),
      ),
    [
      effectivePolicies,
      flagTypePolicies,
      flagAmountThresholds,
      initialPolicies,
    ],
  );

  const validationErrorKey = useMemo(
    () => validateFlagPolicyOverrides(flagTypePolicies, flagAmountThresholds),
    [flagTypePolicies, flagAmountThresholds],
  );

  const upsertThresholdOverride = useCallback(
    (flagType: FlagTypes, patch: Partial<FlagAmountThresholdInput>) => {
      setFlagAmountThresholds((prev) => {
        const existing = findThresholdOverride(prev, flagType);
        const effective = effectivePolicies.find(
          (row) => row.flagType === flagType,
        );
        const nextCents =
          patch.thresholdCents !== undefined
            ? patch.thresholdCents
            : (existing?.thresholdCents ?? effective?.thresholdCents ?? null);
        const nextPercent =
          patch.percentThreshold !== undefined
            ? patch.percentThreshold
            : (existing?.percentThreshold ??
              effective?.percentThreshold ??
              null);

        const override: FlagAmountThresholdInput = { flagType };
        const effectiveCents = effective?.thresholdCents ?? null;
        const effectivePercent = effective?.percentThreshold ?? null;

        if (nextCents !== effectiveCents) {
          override.thresholdCents = nextCents;
        }
        if (nextPercent !== effectivePercent) {
          override.percentThreshold = nextPercent;
        }

        if (
          override.thresholdCents == null &&
          override.percentThreshold == null
        ) {
          return prev.filter((row) => row.flagType !== flagType);
        }

        if (existing) {
          return prev.map((row) =>
            row.flagType === flagType ? override : row,
          );
        }
        return [...prev, override];
      });
    },
    [effectivePolicies],
  );

  const setEnabledControlState = useCallback(
    (flagType: FlagTypes, state: FlagPolicyEnabledState) => {
      if (state === 'UNDEFINED') {
        setFlagAmountThresholds((prev) =>
          prev.filter((row) => row.flagType !== flagType),
        );
        setFlagTypePolicies((prev) =>
          prev.filter((row) => row.flagType !== flagType),
        );
        return;
      }

      if (state === 'OFF') {
        setFlagAmountThresholds((prev) =>
          prev.filter((row) => row.flagType !== flagType),
        );
      }

      if (state === 'ON') {
        setFlagAmountThresholds((prev) =>
          restoreInitialThresholdOnEnable(prev, initialThresholds, flagType),
        );
      }

      setFlagTypePolicies((prev) => {
        const override = buildPolicyOverrideForEnabledState({
          flagType,
          state,
          existing: findPolicyOverride(prev, flagType),
          initialPolicy: findPolicyOverride(initialPolicies, flagType),
          effective: effectivePolicies.find((row) => row.flagType === flagType),
        });

        if (!override) {
          return prev.filter((row) => row.flagType !== flagType);
        }

        const existing = findPolicyOverride(prev, flagType);
        if (existing) {
          return prev.map((row) =>
            row.flagType === flagType ? override : row,
          );
        }

        return [...prev, override];
      });
    },
    [effectivePolicies, initialPolicies, initialThresholds],
  );

  const setEnabled = useCallback(
    (flagType: FlagTypes, enabled: boolean) => {
      setEnabledControlState(flagType, enabled ? 'ON' : 'OFF');
    },
    [setEnabledControlState],
  );

  const setPriority = useCallback(
    (flagType: FlagTypes, priority: FlagPriority) => {
      setFlagTypePolicies((prev) => {
        const effective = effectivePolicies.find(
          (row) => row.flagType === flagType,
        );
        const override: FlagTypePolicyInput = { flagType, enabled: true };
        if (!effective || priority !== effective.priority) {
          override.priority = priority;
        }

        const existing = findPolicyOverride(prev, flagType);
        if (existing) {
          return prev.map((row) =>
            row.flagType === flagType ? override : row,
          );
        }
        return [...prev, override];
      });
    },
    [effectivePolicies],
  );

  const setThresholdCents = useCallback(
    (flagType: FlagTypes, value: number | null) => {
      if (!isThresholdableFlagType(flagType)) {
        return;
      }
      upsertThresholdOverride(flagType, { thresholdCents: value });
    },
    [upsertThresholdOverride],
  );

  const setPercentThreshold = useCallback(
    (flagType: FlagTypes, value: number | null) => {
      if (!isThresholdableFlagType(flagType)) {
        return;
      }
      upsertThresholdOverride(flagType, { percentThreshold: value });
    },
    [upsertThresholdOverride],
  );

  const resetRow = useCallback((flagType: FlagTypes) => {
    setFlagTypePolicies((prev) =>
      prev.filter((row) => row.flagType !== flagType),
    );
    setFlagAmountThresholds((prev) =>
      prev.filter((row) => row.flagType !== flagType),
    );
  }, []);

  const [updateOrgFlagPolicy, { loading: saving }] = useMutation(
    UPDATE_ORG_FLAG_POLICIES,
    {
      onCompleted: async () => {
        const result = await refetchOrg();
        const org = result.data?.fetchOrganization?.organization;
        applyOrgPolicyState(org);
        if (org?.id) {
          setSyncedOrganizationId(org.id);
        }
        showToast({
          message: t('shared:successfullySaved'),
          severity: 'success',
        });
      },
      onError: (error) => {
        showToast({
          message: error.graphQLErrors[0]?.message ?? error.message,
          severity: 'error',
        });
      },
    },
  );

  const save = useCallback(async () => {
    if (validationErrorKey || !organizationId || !canWrite || !isOrgSynced) {
      return;
    }

    return updateOrgFlagPolicy({
      variables: {
        input: {
          organizationId,
          flagTypePolicies: normalizePolicyOverrides(flagTypePolicies),
          flagAmountThresholds:
            normalizeThresholdOverrides(flagAmountThresholds),
        },
      },
    });
  }, [
    organizationId,
    canWrite,
    isOrgSynced,
    validationErrorKey,
    updateOrgFlagPolicy,
    flagTypePolicies,
    flagAmountThresholds,
  ]);

  return {
    rows,
    loading: orgLoading,
    saving,
    isDirty,
    isOrgSynced,
    validationErrorKey,
    canWrite,
    save,
    setEnabled,
    setEnabledControlState,
    setPriority,
    setThresholdCents,
    setPercentThreshold,
    resetRow,
    getThresholdAxis,
    t,
  };
};
